import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Save, Loader2 } from "lucide-react";

export default function LeagueRegistrationControl() {
  const [settings, setSettings] = useState(null);
  const [msg, setMsg] = useState("");
  const [fee, setFee] = useState("");
  const [feeCoins, setFeeCoins] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/league/settings").then(({ data }) => {
      setSettings(data); setMsg(data.closed_message); setFee(String(data.fee));
      setFeeCoins(data.fee_coins_custom ? String(data.fee_coins) : "");
    }).catch(() => {});
  }, []);

  const save = async (open) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/league/settings", {
        registration_open: open, closed_message: msg, fee: Number(fee) || undefined,
        fee_coins: feeCoins.trim() === "" ? 0 : Number(feeCoins),
      });
      setSettings(data); setMsg(data.closed_message); setFee(String(data.fee));
      setFeeCoins(data.fee_coins_custom ? String(data.fee_coins) : "");
      toast.success(open ? "League registration payment is OPEN — members can pay & join" : "League registration payment is CLOSED");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!settings) return null;
  const open = settings.registration_open;
  return (
    <div className="mt-8 bg-pb-surface border border-pb-border p-6" data-testid="league-reg-control">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="w-5 h-5 text-pb-red" />
        <h3 className="font-display font-bold text-xl uppercase">Registration Payment Control</h3>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Controls the entry-fee box and "Join the League" payment button on the League Roster form. When closed, members see your message instead.</p>

      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Registration Fee</label>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center bg-pb-elevated border border-pb-border focus-within:border-pb-red w-40">
          <span className="pl-3 text-zinc-500 text-sm">$</span>
          <input data-testid="league-reg-fee-input" type="number" min={1} max={10000} step="0.01" value={fee}
            onChange={(e) => setFee(e.target.value)}
            className="w-full bg-transparent px-2 py-2.5 text-white focus:outline-none font-mono" />
        </div>
        <div className="flex items-center bg-pb-elevated border border-pb-border focus-within:border-purple-400 w-48">
          <input data-testid="league-reg-fee-coins-input" type="number" min={0} max={1000000} step="0.01" value={feeCoins}
            placeholder={settings.fee_coins_custom ? "0" : `auto: ${settings.fee_coins ?? "—"}`}
            onChange={(e) => setFeeCoins(e.target.value)}
            className="w-full bg-transparent pl-3 pr-2 py-2.5 text-white focus:outline-none font-mono" />
          <span className="pr-3 text-purple-300 text-xs font-bold">BLC</span>
        </div>
        <button data-testid="league-reg-fee-save" onClick={() => save(settings.registration_open)} disabled={busy}
          className="shrink-0 flex items-center gap-1.5 bg-pb-elevated border border-pb-border px-4 text-xs font-bold uppercase tracking-widest hover:border-pb-red transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Fee
        </button>
        <span className="text-[11px] text-zinc-600 self-center basis-full" data-testid="league-fee-coin-note">
          Members pay <b className="text-zinc-400">${Number(fee || settings.fee).toFixed(2)}</b> by card or <b className="text-purple-300">{settings.fee_coins ? settings.fee_coins.toLocaleString() : "—"} BLC</b> by coin{settings.fee_coins_custom ? " (your custom coin fee)" : " (auto-converted at the current BLC rate)"}. Leave the BLC box empty to auto-convert.
        </span>
      </div>

      <button data-testid="league-reg-toggle" onClick={() => save(!open)} disabled={busy}
        className={`w-full flex items-center justify-between border px-4 py-3 mb-4 transition-colors disabled:opacity-60 ${open ? "border-pb-cyan bg-pb-cyan/10" : "border-pb-red bg-pb-red/5"}`}>
        <span className="text-xs font-bold uppercase tracking-widest text-zinc-300">
          Registration payment is <span className={open ? "text-pb-cyan" : "text-pb-red"}>{open ? "OPEN" : "CLOSED"}</span>
        </span>
        <span className={`relative inline-flex h-5 w-10 shrink-0 rounded-full transition-colors ${open ? "bg-pb-cyan" : "bg-pb-border"}`}>
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-black transition-transform ${open ? "translate-x-5" : "translate-x-0.5"}`} />
        </span>
      </button>

      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Message shown when closed</label>
      <div className="flex gap-2">
        <textarea data-testid="league-reg-closed-msg" rows={2} value={msg} onChange={(e) => setMsg(e.target.value)}
          className="flex-1 bg-pb-elevated border border-pb-border px-3 py-2 text-sm focus:outline-none focus:border-pb-red resize-none" />
        <button data-testid="league-reg-msg-save" onClick={() => save(open)} disabled={busy}
          className="shrink-0 flex items-center gap-1.5 bg-pb-elevated border border-pb-border px-4 text-xs font-bold uppercase tracking-widest hover:border-pb-red transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
        </button>
      </div>
    </div>
  );
}
